import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Play, ChevronDown, Clock, MousePointer, Search, Download, Filter, BarChart2, UserPlus, ChevronUp, Info, X, Calendar, Phone, Mail, Building, DollarSign, Tag, Award, User, Globe, Smartphone, RefreshCw } from 'lucide-react';
import SignalChart from '../components/SignalChart';
const UserDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'sessions' | 'signals'>('sessions');
  const [expandedSession, setExpandedSession] = useState<number | null>(0);
  const [selectedAction, setSelectedAction] = useState<number | null>(2);
  // Mock user data
  const user = {
    id: parseInt(id || '1'),
    name: 'Jane Smith',
    email: 'jane.smith@initech.com',
    account: 'Initech',
    accountId: 1,
    role: 'Admin',
    lastActive: '2023-05-10',
    sessions: 128,
    status: 'Active',
    createdAt: 'Jan 15, 2023',
    lastSeen: 'May 10, 2023',
    firstSeen: 'Jul 17, 2022',
    phone: '(555) 123-4567',
    location: 'San Francisco, CA',
    timezone: 'Pacific Time (PT)',
    browser: 'Chrome 96.0.4664.110',
    os: 'macOS 12.0.1',
    device: 'Apple MacBook Pro',
    userId: '711453733184508733',
    amplitudeId: '1272354940628',
    deviceId: '3bdc1285-a2cb-4090-aa14-835a344d8e8',
    country: 'United States',
    carrier: 'AT&T',
    purchases: 15,
    stage: 'Qualified',
    opportunity: '$75,000',
    plan: 'Enterprise',
    owner: 'John Smith',
    renewal: '12/14/2023',
    nps: 9,
    lifetimeValue: '$175,000',
    openTickets: 2,
    accountHealth: 'Good',
    lastContact: 'Apr 28, 2023',
    nextMeeting: 'May 15, 2023'
  };
  // Mock session data
  const recentSession = {
    id: 1,
    date: 'May 10, 2025',
    time: '10:30 AM',
    duration: '45m',
    actions: 23,
    device: 'Desktop',
    os: 'macOS 12.0.1',
    deviceType: 'Apple MacBook Pro',
    deviceId: '3bdc1285-a2cb-4090-aa14-835a344d8e8',
    events: [{
      id: 1,
      time: '10:30 AM',
      type: 'login',
      description: 'Logged in',
      color: 'blue'
    }, {
      id: 2,
      time: '10:31 AM',
      type: 'view',
      description: 'Viewed dashboard',
      color: 'blue'
    }, {
      id: 3,
      time: '10:33 AM',
      type: 'click',
      description: "Clicked 'New Report' button",
      color: 'green'
    }, {
      id: 4,
      time: '10:35 AM',
      type: 'select',
      description: "Selected 'Sales Performance' template",
      color: 'green'
    }, {
      id: 5,
      time: '10:38 AM',
      type: 'filter',
      description: 'Added filter by region',
      color: 'yellow'
    }, {
      id: 6,
      time: '10:42 AM',
      type: 'generate',
      description: 'Generated report',
      color: 'yellow'
    }, {
      id: 7,
      time: '10:45 AM',
      type: 'download',
      description: 'Downloaded report as PDF',
      color: 'purple'
    }, {
      id: 8,
      time: '10:48 AM',
      type: 'navigate',
      description: "Navigated to 'User Management'",
      color: 'blue'
    }, {
      id: 9,
      time: '10:52 AM',
      type: 'invite',
      description: 'Invited new user',
      color: 'yellow'
    }, {
      id: 10,
      time: '10:55 AM',
      type: 'view',
      description: "Viewed 'Settings' page",
      color: 'blue'
    }]
  };
  // Updated signal chart data with realistic patterns
  const signalChartData = [{
    id: 1,
    title: 'Feature Usage',
    type: 'bar',
    subtitle: 'Reports Created',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Mon',
      value: 3
    }, {
      date: 'Tue',
      value: 5
    }, {
      date: 'Wed',
      value: 2
    }, {
      date: 'Thu',
      value: 7
    }, {
      date: 'Fri',
      value: 4
    }, {
      date: 'Sat',
      value: 1
    }, {
      date: 'Sun',
      value: 0
    }],
    trend: 'up',
    percentage: 15,
    timeframe: 'vs last week'
  }, {
    id: 2,
    title: 'Login Activity',
    type: 'line',
    subtitle: 'Sessions Over Time',
    actionType: 'Authentication',
    timeSeriesData: [{
      date: 'Jan 2',
      value: 2
    }, {
      date: 'Jan 9',
      value: 5
    }, {
      date: 'Jan 16',
      value: 4
    }, {
      date: 'Jan 23',
      value: 8
    }, {
      date: 'Jan 30',
      value: 6
    }, {
      date: 'Feb 6',
      value: 9
    }, {
      date: 'Feb 13',
      value: 7
    }, {
      date: 'Feb 20',
      value: 10
    }, {
      date: 'Feb 27',
      value: 12
    }],
    trend: 'up',
    percentage: 33,
    timeframe: 'vs previous period'
  }, {
    id: 3,
    title: 'Dashboard Interactions',
    type: 'bar',
    subtitle: 'Filter & Sort Actions',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Mon',
      value: 8
    }, {
      date: 'Tue',
      value: 12
    }, {
      date: 'Wed',
      value: 7
    }, {
      date: 'Thu',
      value: 15
    }, {
      date: 'Fri',
      value: 10
    }, {
      date: 'Sat',
      value: 3
    }, {
      date: 'Sun',
      value: 2
    }],
    trend: 'up',
    percentage: 8,
    timeframe: 'vs last week'
  }, {
    id: 4,
    title: 'Feature Adoption',
    type: 'line',
    subtitle: 'New Features Used',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Jan',
      value: 2
    }, {
      date: 'Feb',
      value: 3
    }, {
      date: 'Mar',
      value: 5
    }, {
      date: 'Apr',
      value: 7
    }, {
      date: 'May',
      value: 8
    }, {
      date: 'Jun',
      value: 10
    }, {
      date: 'Jul',
      value: 12
    }, {
      date: 'Aug',
      value: 14
    }, {
      date: 'Sep',
      value: 15
    }],
    trend: 'up',
    percentage: 25,
    timeframe: 'vs last 6 months'
  }];
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'login':
        return <User size={16} className="text-blue-500" data-id="element-1808" />;
      case 'view':
        return <Search size={16} className="text-blue-500" data-id="element-1809" />;
      case 'click':
        return <MousePointer size={16} className="text-green-500" data-id="element-1810" />;
      case 'select':
        return <MousePointer size={16} className="text-green-500" data-id="element-1811" />;
      case 'filter':
        return <Filter size={16} className="text-yellow-500" data-id="element-1812" />;
      case 'generate':
        return <BarChart2 size={16} className="text-yellow-500" data-id="element-1813" />;
      case 'download':
        return <Download size={16} className="text-purple-500" data-id="element-1814" />;
      case 'navigate':
        return <RefreshCw size={16} className="text-blue-500" data-id="element-1815" />;
      case 'invite':
        return <UserPlus size={16} className="text-yellow-500" data-id="element-1816" />;
      default:
        return <Clock size={16} className="text-gray-500" data-id="element-1817" />;
    }
  };
  const getEventColor = (type: string) => {
    switch (type) {
      case 'login':
      case 'view':
      case 'navigate':
        return 'bg-blue-500';
      case 'click':
      case 'select':
        return 'bg-green-500';
      case 'filter':
      case 'generate':
      case 'invite':
        return 'bg-yellow-500';
      case 'download':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };
  const toggleSession = (sessionId: number) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
    } else {
      setExpandedSession(sessionId);
    }
  };
  const handleSelectAction = (actionId: number) => {
    setSelectedAction(actionId);
  };
  return <div className="flex h-full" data-id="element-1818">
      {/* Left sidebar with user properties - now extends to the top */}
      <div className="w-80 border-r border-gray-200 overflow-y-auto bg-white" data-id="element-1819">
        <div className="p-4" data-id="element-1820">
          <div className="flex flex-col items-center mb-6" data-id="element-1821">
            <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mb-2" data-id="element-1822">
              <span className="text-3xl" data-id="element-1823">👤</span>
            </div>
            <h2 className="text-lg font-medium text-gray-900" data-id="element-1824">{user.name}</h2>
            <p className="text-sm text-gray-600" data-id="element-1825">{user.email}</p>
          </div>
          <div className="space-y-6" data-id="element-1826">
            <div className="space-y-3" data-id="element-1827">
              <h3 className="text-sm font-medium text-gray-700 flex items-center" data-id="element-1828">
                <User size={16} className="text-gray-400 mr-2" data-id="element-1829" />
                Contact Information
              </h3>
              <div className="space-y-2 pl-6" data-id="element-1830">
                <div className="flex items-center" data-id="element-1831">
                  <Mail size={14} className="text-gray-400 mr-2" data-id="element-1832" />
                  <span className="text-sm text-gray-600" data-id="element-1833">Email</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1834">
                    {user.email}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1835">
                  <Phone size={14} className="text-gray-400 mr-2" data-id="element-1836" />
                  <span className="text-sm text-gray-600" data-id="element-1837">Phone</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1838">
                    {user.phone}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1839">
                  <Globe size={14} className="text-gray-400 mr-2" data-id="element-1840" />
                  <span className="text-sm text-gray-600" data-id="element-1841">Location</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1842">
                    {user.location}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-3" data-id="element-1843">
              <h3 className="text-sm font-medium text-gray-700 flex items-center" data-id="element-1844">
                <Building size={16} className="text-gray-400 mr-2" data-id="element-1845" />
                Account Details
              </h3>
              <div className="space-y-2 pl-6" data-id="element-1846">
                <div className="flex items-center" data-id="element-1847">
                  <Building size={14} className="text-gray-400 mr-2" data-id="element-1848" />
                  <span className="text-sm text-gray-600" data-id="element-1849">Company</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1850">
                    {user.account}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1851">
                  <DollarSign size={14} className="text-gray-400 mr-2" data-id="element-1852" />
                  <span className="text-sm text-gray-600" data-id="element-1853">Plan</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1854">
                    {user.plan}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1855">
                  <Tag size={14} className="text-gray-400 mr-2" data-id="element-1856" />
                  <span className="text-sm text-gray-600" data-id="element-1857">Stage</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1858">
                    {user.stage}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1859">
                  <Award size={14} className="text-gray-400 mr-2" data-id="element-1860" />
                  <span className="text-sm text-gray-600" data-id="element-1861">NPS</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1862">
                    {user.nps}/10
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-3" data-id="element-1863">
              <h3 className="text-sm font-medium text-gray-700 flex items-center" data-id="element-1864">
                <Calendar size={16} className="text-gray-400 mr-2" data-id="element-1865" />
                Important Dates
              </h3>
              <div className="space-y-2 pl-6" data-id="element-1866">
                <div className="flex items-center" data-id="element-1867">
                  <Calendar size={14} className="text-gray-400 mr-2" data-id="element-1868" />
                  <span className="text-sm text-gray-600" data-id="element-1869">First Seen</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1870">
                    {user.firstSeen}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1871">
                  <Clock size={14} className="text-gray-400 mr-2" data-id="element-1872" />
                  <span className="text-sm text-gray-600" data-id="element-1873">Last Seen</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1874">
                    {user.lastSeen}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1875">
                  <Calendar size={14} className="text-gray-400 mr-2" data-id="element-1876" />
                  <span className="text-sm text-gray-600" data-id="element-1877">Next Meeting</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1878">
                    {user.nextMeeting}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1879">
                  <Calendar size={14} className="text-gray-400 mr-2" data-id="element-1880" />
                  <span className="text-sm text-gray-600" data-id="element-1881">Renewal</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1882">
                    {user.renewal}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-3" data-id="element-1883">
              <h3 className="text-sm font-medium text-gray-700 flex items-center" data-id="element-1884">
                <Smartphone size={16} className="text-gray-400 mr-2" data-id="element-1885" />
                Technical Details
              </h3>
              <div className="space-y-2 pl-6" data-id="element-1886">
                <div className="flex items-center" data-id="element-1887">
                  <Info size={14} className="text-gray-400 mr-2" data-id="element-1888" />
                  <span className="text-sm text-gray-600" data-id="element-1889">User ID</span>
                  <span className="text-sm text-gray-900 ml-auto truncate max-w-[150px]" title={user.userId} data-id="element-1890">
                    {user.userId}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1891">
                  <Smartphone size={14} className="text-gray-400 mr-2" data-id="element-1892" />
                  <span className="text-sm text-gray-600" data-id="element-1893">Device</span>
                  <span className="text-sm text-gray-900 ml-auto" data-id="element-1894">
                    {user.device}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1895">
                  <Globe size={14} className="text-gray-400 mr-2" data-id="element-1896" />
                  <span className="text-sm text-gray-600" data-id="element-1897">Browser</span>
                  <span className="text-sm text-gray-900 ml-auto truncate max-w-[150px]" title={user.browser} data-id="element-1898">
                    {user.browser}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1899">
        {/* Header with back button */}
        <div className="p-4 border-b border-gray-200" data-id="element-1900">
          <div className="flex items-center justify-between" data-id="element-1901">
            <button onClick={() => navigate(`/accounts/${user.accountId}`)} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-1902">
              <ArrowLeft size={16} className="mr-1" data-id="element-1903" />
              <span className="text-sm" data-id="element-1904">Back to Accounts</span>
            </button>
            <div className="flex space-x-6" data-id="element-1905">
              <div className="text-right" data-id="element-1906">
                <p className="text-xs text-gray-500" data-id="element-1907">ARR</p>
                <p className="font-medium" data-id="element-1908">{user.opportunity}</p>
              </div>
              <div className="text-right" data-id="element-1909">
                <p className="text-xs text-gray-500" data-id="element-1910">Plan</p>
                <p className="font-medium" data-id="element-1911">{user.plan}</p>
              </div>
              <div className="text-right" data-id="element-1912">
                <p className="text-xs text-gray-500" data-id="element-1913">Owner</p>
                <p className="font-medium" data-id="element-1914">{user.owner}</p>
              </div>
              <div className="text-right" data-id="element-1915">
                <p className="text-xs text-gray-500" data-id="element-1916">Renewal</p>
                <p className="font-medium" data-id="element-1917">{user.renewal}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab navigation - now positioned to the right of the sidebar */}
        <div className="border-b border-gray-200" data-id="element-1918">
          <div className="flex" data-id="element-1919">
            <button className={`py-3 px-6 text-sm font-medium border-b-2 ${activeTab === 'sessions' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('sessions')} data-id="element-1920">
              User Sessions
            </button>
            <button className={`py-3 px-6 text-sm font-medium border-b-2 ${activeTab === 'signals' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('signals')} data-id="element-1921">
              Signals
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto" data-id="element-1922">
          {activeTab === 'sessions' ? <div className="p-4" data-id="element-1923">
              <div className="mb-4" data-id="element-1924">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-1925">
                  Recent Sessions
                </h2>
              </div>
              <div className="bg-white border border-gray-200 rounded-md" data-id="element-1926">
                <div className="p-4 border-b border-gray-200" data-id="element-1927">
                  <div className="flex items-center" data-id="element-1928">
                    <Clock size={16} className="text-gray-500 mr-2" data-id="element-1929" />
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-1930">
                      {recentSession.date} - {recentSession.time}
                    </h3>
                    <span className="ml-2 text-xs text-gray-500" data-id="element-1931">
                      Duration: {recentSession.duration} • Actions:{' '}
                      {recentSession.actions}
                    </span>
                  </div>
                </div>
                <div className="flex" data-id="element-1932">
                  {/* Timeline view */}
                  <div className="w-1/2 border-r border-gray-200" data-id="element-1933">
                    <div className="p-3 border-b border-gray-200" data-id="element-1934">
                      <h4 className="text-xs font-medium text-gray-700" data-id="element-1935">
                        Action Timeline
                      </h4>
                    </div>
                    <div className="overflow-y-auto" style={{
                  maxHeight: '500px'
                }} data-id="element-1936">
                      {recentSession.events.map(event => <div key={event.id} className={`flex items-start p-3 border-b border-gray-100 cursor-pointer ${selectedAction === event.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`} onClick={() => handleSelectAction(event.id)} data-id="element-1937">
                          <div className="flex-shrink-0 mr-3" data-id="element-1938">
                            <div className="text-xs text-gray-500" data-id="element-1939">
                              {event.time}
                            </div>
                          </div>
                          <div className="mr-3 flex-shrink-0 mt-1" data-id="element-1940">
                            <div className={`w-2 h-2 rounded-full ${getEventColor(event.type)}`} data-id="element-1941"></div>
                          </div>
                          <div className="flex-1" data-id="element-1942">
                            <div className="flex items-center" data-id="element-1943">
                              {getEventIcon(event.type)}
                              <span className="ml-2 text-sm text-gray-900" data-id="element-1944">
                                {event.description}
                              </span>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Visual replay */}
                  <div className="w-1/2" data-id="element-1945">
                    <div className="p-3 border-b border-gray-200" data-id="element-1946">
                      <h4 className="text-xs font-medium text-gray-700" data-id="element-1947">
                        Visual Replay
                      </h4>
                    </div>
                    <div className="p-4" data-id="element-1948">
                      {selectedAction ? <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-1949">
                          <div className="bg-gray-100 p-1 flex items-center space-x-1" data-id="element-1950">
                            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1951"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1952"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-1953"></div>
                            <div className="mx-auto text-xs text-gray-600" data-id="element-1954">
                              https://salesforce.com/lightning/opportunity
                            </div>
                          </div>
                          <div className="bg-white h-80 overflow-hidden" data-id="element-1955">
                            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rxyErhw1SF7i5F6WP41ksR/Salesforce2BLightning-2791053328.png" alt="Salesforce Lightning Interface" className="w-full h-full object-cover" data-id="element-1956" />
                          </div>
                        </div> : <div className="flex items-center justify-center h-80 text-gray-500" data-id="element-1957">
                          Select an action from the timeline to see the visual
                          replay
                        </div>}
                    </div>
                  </div>
                </div>
              </div>
            </div> : <div className="p-6" data-id="element-1958">
              <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1959">
                User Activity Signals
              </h2>
              <div className="grid grid-cols-2 gap-6" data-id="element-1960">
                {signalChartData.map(chart => <SignalChart key={chart.id} data={chart} data-id="element-1961" />)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default UserDetail;